import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Pencil, X } from 'lucide-react';
import { updateTicket, type TicketRecord } from '@/lib/api/tickets.js';
import { parseApiError } from '@/lib/api/errors.js';
import { formatDateTime } from '@/lib/format.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { SnippetTextarea } from '@/components/common/SnippetTextarea.js';

function staffShortName(first?: string | null, last?: string | null): string | null {
  const f = first?.trim();
  if (!f) return null;
  const lastInit = last?.trim()?.[0];
  return lastInit ? `${f} ${lastInit.toUpperCase()}` : f;
}

function clipComplaint(value: string): string {
  return value.slice(0, 500);
}

export function TicketIntakeContext({
  ticket,
  onSaved,
  compact = false,
}: {
  ticket: TicketRecord;
  onSaved: () => void;
  compact?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [customerComplaint, setCustomerComplaint] = useState('');
  const [intakeNotes, setIntakeNotes] = useState('');

  useEffect(() => {
    if (editing) return;
    setCustomerComplaint(ticket.customerComplaint ?? '');
    setIntakeNotes(ticket.intakeNotes ?? '');
  }, [ticket.customerComplaint, ticket.intakeNotes, editing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      const nextComplaint = clipComplaint(customerComplaint.trim()) || null;
      const nextNotes = intakeNotes.trim() || null;
      if (nextComplaint !== (ticket.customerComplaint ?? null)) {
        body.customerComplaint = nextComplaint;
      }
      if (nextNotes !== (ticket.intakeNotes ?? null)) {
        body.intakeNotes = nextNotes;
      }
      if (Object.keys(body).length === 0) return { unchanged: true as const };
      await updateTicket(ticket.id, body);
      return { unchanged: false as const };
    },
    onSuccess: (result) => {
      if (result?.unchanged) {
        setEditing(false);
        return;
      }
      toastSuccess('Intake notes updated');
      setEditing(false);
      onSaved();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    saveMutation.mutate();
  }

  const recordedBy = staffShortName(ticket.createdByFirstName, ticket.createdByLastName);
  const recordedAt = formatDateTime(ticket.createdAt);
  const attribution = recordedBy
    ? `recorded at intake by ${recordedBy}, ${recordedAt}`
    : `recorded at intake, ${recordedAt}`;

  const complaint = ticket.customerComplaint?.trim() || '';
  const notes = ticket.intakeNotes?.trim() || '';

  return (
    <section className="space-y-3">
      <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-xs font-bold uppercase tracking-wide text-primary">
            Customer Complaint
          </h2>
          {!editing && !compact ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setCustomerComplaint(ticket.customerComplaint ?? '');
                setIntakeNotes(ticket.intakeNotes ?? '');
                setEditing(true);
              }}
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
          ) : null}
        </div>

        {editing ? (
          <form className="mt-3 space-y-4" onSubmit={handleSubmit}>
            <label className="block">
              <span className="text-sm font-medium text-slate-800">What the customer said</span>
              <SnippetTextarea
                className="mt-1 min-h-24 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                value={customerComplaint}
                maxLength={500}
                onChange={(value) => setCustomerComplaint(clipComplaint(value))}
                placeholder="Dropped in water last night. Screen flickers…"
              />
              <span className="mt-1 block text-xs text-slate-500">
                {customerComplaint.length}/500 · shown to the customer on the portal
              </span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-800">Internal diagnostic notes</span>
              <SnippetTextarea
                className="mt-1 min-h-20 w-full rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
                value={intakeNotes}
                onChange={setIntakeNotes}
                placeholder="Staff observations — not shown to the customer"
              />
              <span className="mt-1 block text-xs text-slate-500">Staff only — never shown on the customer portal</span>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  setEditing(false);
                  setCustomerComplaint(ticket.customerComplaint ?? '');
                  setIntakeNotes(ticket.intakeNotes ?? '');
                }}
                disabled={saveMutation.isPending}
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        ) : complaint ? (
          <>
            <p className="mt-3 whitespace-pre-wrap text-base font-medium leading-relaxed text-slate-950">
              “{complaint}”
            </p>
            <p className="mt-2 text-xs text-slate-500">— {attribution}</p>
          </>
        ) : (
          <p className="mt-3 text-sm italic text-slate-500">No customer complaint recorded at intake.</p>
        )}
      </div>

      {!editing && notes ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
          <h2 className="text-xs font-bold uppercase tracking-wide text-amber-800">
            Internal Diagnostic Notes
          </h2>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-amber-950">{notes}</p>
          <p className="mt-2 text-xs text-amber-800/80">Staff only — not shown to the customer</p>
        </div>
      ) : null}
    </section>
  );
}
